import { useMemo, useState } from 'react';
import { Eye, FileSpreadsheet, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  ErrorDialog,
  SuccessDialog,
} from '@/shared/components/ui/status-dialog';
import { cn } from '@/shared/lib/cn';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import type { ContactoItem } from '@/shared/api/controllers/contactos';
import { ContactDialog } from './ContactDialog';
import { ContactViewDialog } from './ContactViewDialog';
import { DeleteContactDialog } from './DeleteContactDialog';
import { useActiveContactos } from './queries';

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; contact: ContactoItem }
  | { kind: 'view'; contact: ContactoItem }
  | { kind: 'delete'; contact: ContactoItem };

export function ContactosPage() {
  const isReadOnly = useIsReadOnly();
  const { data: contactos = [], isPending, isFetching, error, refetch } = useActiveContactos();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });
  const [status, setStatus] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [...contactos].sort((a, b) =>
      (a.fields.NombreApellido_C ?? '').localeCompare(b.fields.NombreApellido_C ?? '', 'es'),
    );
    if (!q) return rows;
    return rows.filter((c) => {
      const nombre = (c.fields.NombreApellido_C ?? '').toLowerCase();
      const mail = (c.fields.Mail_C ?? '').toLowerCase();
      const cel = (c.fields.Celular_C ?? '').toLowerCase();
      return nombre.includes(q) || mail.includes(q) || cel.includes(q);
    });
  }, [contactos, search]);

  const closeDialog = () => setMode({ kind: 'closed' });

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex shrink-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Contactos</h1>
        <div className="flex w-full items-center gap-3 md:w-auto">
          {!isReadOnly && (
            <Button
              onClick={() => setMode({ kind: 'create' })}
              className="h-12 shrink-0 gap-2 bg-foreground px-4 text-background hover:bg-foreground/90"
            >
              Nuevo Contacto
              <Plus className="h-4 w-4" aria-hidden />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 shrink-0"
            aria-label="Descargar base en Excel"
            title="Descargar base en Excel"
            onClick={() => void exportContactosToXlsx(filtered)}
            disabled={filtered.length === 0}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
          </Button>
          <div className="relative flex-1 md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              variant="filled"
              placeholder="Búsqueda"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar contacto"
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
        {/* Desktop / tablet table */}
        <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <Th>Nombre</Th>
                <Th>Mail</Th>
                <Th>Celular</Th>
                <Th className="w-32 text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isPending ? (
                <RowStatus>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Cargando contactos…
                </RowStatus>
              ) : error ? (
                <RowStatus>
                  <span className="text-destructive">No se pudieron cargar los contactos.</span>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Reintentar
                  </Button>
                </RowStatus>
              ) : filtered.length === 0 ? (
                <RowStatus>
                  {search
                    ? `No hay resultados para "${search}".`
                    : 'Todavía no hay contactos. Agregá uno con Nuevo Contacto.'}
                </RowStatus>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="transition-colors hover:bg-muted/30">
                    <Td className="font-medium text-foreground">
                      {c.fields.NombreApellido_C || '—'}
                    </Td>
                    <Td className="text-muted-foreground">{c.fields.Mail_C || '—'}</Td>
                    <Td className="text-muted-foreground">{c.fields.Celular_C || '—'}</Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {!isReadOnly && (
                          <IconAction
                            label="Editar"
                            onClick={() => setMode({ kind: 'edit', contact: c })}
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </IconAction>
                        )}
                        <IconAction
                          label="Ver"
                          onClick={() => setMode({ kind: 'view', contact: c })}
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </IconAction>
                        {!isReadOnly && (
                          <IconAction
                            label="Eliminar"
                            tone="destructive"
                            onClick={() => setMode({ kind: 'delete', contact: c })}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </IconAction>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
          {isPending ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Cargando contactos…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
              <span>No se pudieron cargar los contactos.</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Reintentar
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {search
                ? `No hay resultados para "${search}".`
                : 'Todavía no hay contactos. Agregá uno con Nuevo Contacto.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((c) => (
                <li key={c.id} className="flex flex-col gap-1.5 px-4 py-3">
                  <div className="font-medium text-foreground">
                    {c.fields.NombreApellido_C || '—'}
                  </div>
                  {c.fields.Mail_C && (
                    <div className="truncate text-xs text-muted-foreground">
                      {c.fields.Mail_C}
                    </div>
                  )}
                  {c.fields.Celular_C && (
                    <div className="text-xs text-muted-foreground">{c.fields.Celular_C}</div>
                  )}
                  <div className="flex items-center justify-end gap-1 pt-1">
                    {!isReadOnly && (
                      <IconAction
                        label="Editar"
                        onClick={() => setMode({ kind: 'edit', contact: c })}
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </IconAction>
                    )}
                    <IconAction
                      label="Ver"
                      onClick={() => setMode({ kind: 'view', contact: c })}
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </IconAction>
                    {!isReadOnly && (
                      <IconAction
                        label="Eliminar"
                        tone="destructive"
                        onClick={() => setMode({ kind: 'delete', contact: c })}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </IconAction>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {isFetching && !isPending && (
          <div className="flex shrink-0 items-center justify-center gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Actualizando…
          </div>
        )}
      </div>

      <ContactDialog
        open={mode.kind === 'create' || mode.kind === 'edit'}
        onOpenChange={(o) => !o && closeDialog()}
        contact={mode.kind === 'edit' ? mode.contact : null}
        existingContacts={contactos}
        onSuccess={(message) => setStatus({ kind: 'success', message })}
        onError={(message) => setStatus({ kind: 'error', message })}
      />
      <ContactViewDialog
        open={mode.kind === 'view'}
        onOpenChange={(o) => !o && closeDialog()}
        contact={mode.kind === 'view' ? mode.contact : null}
      />
      <DeleteContactDialog
        open={mode.kind === 'delete'}
        onOpenChange={(o) => !o && closeDialog()}
        contact={mode.kind === 'delete' ? mode.contact : null}
        onSuccess={(message) => setStatus({ kind: 'success', message })}
        onError={(message) => setStatus({ kind: 'error', message })}
      />

      <SuccessDialog
        open={status?.kind === 'success'}
        message={status?.kind === 'success' ? status.message : ''}
        onClose={() => setStatus(null)}
      />
      <ErrorDialog
        open={status?.kind === 'error'}
        message={status?.kind === 'error' ? status.message : ''}
        onClose={() => setStatus(null)}
      />
    </div>
  );
}

/**
 * Download the (search-filtered) contact base as a real .xlsx — the client
 * asked for "descargar la base en xls". Mirrors the Ventas / Inventario
 * export buttons via the shared `downloadXlsx` helper. Already country-
 * scoped: `useActiveContactos` only returns the active country's contacts.
 */
async function exportContactosToXlsx(rows: ContactoItem[]): Promise<void> {
  const headers = [
    'Nombre y Apellido',
    'Razón Social',
    'CUIT',
    'Mail',
    'Celular',
    'Dirección',
    'Ciudad',
    'Provincia',
    'País',
    'Código Postal',
    'Medio de Llegada',
    'Referido',
    'Observaciones',
  ];
  const data = rows.map((c) => {
    const f = c.fields;
    return [
      f.NombreApellido_C ?? `${f.Nombre_C ?? ''} ${f.Apellido_C ?? ''}`.trim(),
      f.RazonSocial_C ?? '',
      f.CUIT_C ?? '',
      f.Mail_C ?? '',
      f.Celular_C ?? '',
      f.DireccionCompleta_C ?? f.Direccion_C ?? '',
      f.Ciudad_C ?? '',
      f.Provincia_C ?? '',
      f.Pais_C ?? '',
      f.CodigoPostal_C ?? f.CP_C ?? '',
      f.MedioDeLlegada_C ?? '',
      f.Referido_C ?? '',
      f.Observaciones_C ?? '',
    ];
  });
  const today = fechaArchivoHoy();
  await downloadXlsx({
    filename: `contactos_${today}.xlsx`,
    sheetName: 'Contactos',
    headers,
    rows: data,
  });
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-4 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3', className)}>{children}</td>;
}

function RowStatus({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={4} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function IconAction({
  label,
  onClick,
  tone,
  children,
}: {
  label: string;
  onClick: () => void;
  tone?: 'destructive';
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        tone === 'destructive'
          ? 'border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20'
          : 'border border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
